import { GA_TRACKING_ID, ODDLE_PASS_CLIENT_ID } from '@/libs/config/client'

import {
  selectDetailMenuSite,
  selectIsBot,
  selectTrackers,
} from '@/redux/meta/selector'
import { decodeToken } from '@/utils/jwt'
import { isObjectEmpty } from '@/utils/object'
import NextRouter from 'next/router'
import { useSelector } from 'react-redux'
import { useGetTrackersQuery } from '@/services/oms'
import { getFullProductDetails, getOrderItemQuantity } from '@/utils/product'
import { selectMenuData } from '@/redux/menu'

const name = 'Oddle Shop'
const safeGtag = (command: string, targetId: string, config?: any): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(command, targetId, config)
  }
}

const safeFbq = (command: string, targetId: string, config?: any): void => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq(command, targetId, config)
  }
}

const safeSegment = (callback: (analytics: any) => void): void => {
  if (typeof window === 'undefined' || !window.analytics) {
    return
  }
  callback(window.analytics)
}

export type UseTracker = {
  notifyPageView: () => void
  notifyViewContent: (productId?: string) => void
  // notifyInitiateCheckout: () => void
  notifyPurchase: (storeOrder?: Oms.StoreOrder | null) => void
  segmentTrackerCheckoutStarted: (data: Record<string, unknown>) => void
  segmentTrackerSignedOut: (payload: { country: string }) => void
  segmentTrackerSignedIn: (payload: {
    id: string
    email: string
    firstName: string
    lastName: string
    phone: string
    continueWithOddlePass: boolean
    country: string
    phoneIsVerified: boolean
    authMethod: string
    action?: string
  }) => void
  segmentTrackerMenuViewed: () => void
  segmentTrackerOrderReminderSaved: (
    payload: Oms.StoreOrder & {
      orderReminderDestination: string
    }
  ) => void
  segmentTrackerOrderedShare: (
    payload: Oms.StoreOrder & { appName: string }
  ) => void
  segmentTrackerOrderViewed: (
    payload: Oms.StoreOrder & { appName?: string; isPurchaser: boolean }
  ) => void
  notifyAddToCart: (payload: {
    menuItem: {
      id: string
      name: string
      imageFullUrls?: string[]
      imageUrls?: string[]
    }
    orderItem: {
      quantity: number
    }
    finalPrice: number
  }) => void
  notifyNewsletterCompleteRegistration: (
    payload: Record<string, unknown>
  ) => void
  notifyNewsletterLead: () => void
  notifyAccountCompleteRegistration: (payload: { facebookId?: string }) => void
  notifyAccountLead: () => void
  notifyViewCart: (storeOrder: Oms.StoreOrder) => void
  notifyLogin: (method: string) => void
}

const useTracker = (): UseTracker => {
  const menuDetailsInfo = useSelector(selectDetailMenuSite)
  useGetTrackersQuery(menuDetailsInfo?.menu?.id, {
    skip: !menuDetailsInfo?.menu?.id,
  })
  // Replace the general state selector with specific selectors
  const trackers = useSelector(selectTrackers)
  const menuData = useSelector(selectMenuData)
  // const checkoutMetaData = useSelector(
  //   (state: RootState) => state['@checkout'].meta.data
  // )
  // const checkoutResults = useSelector(
  //   (state: RootState) => state['@checkout'].checkoutResults
  // )
  // const thankyouResults = useSelector(
  //   (state: RootState) => state['@thankyou'].checkoutResults
  // )

  const meta = useSelector(selectDetailMenuSite)
  const isBot = useSelector(selectIsBot)

  const notifyPageView = () => {
    try {
      // Only run on client side to prevent hydration errors
      if (typeof window === 'undefined') return

      const ga = (trackers || []).find(
        (t) => t.trackingClient === 'GoogleAnalytics'
      )
      let customerId: string | undefined
      if (
        typeof window !== 'undefined' &&
        window.localStorage.getItem('accessToken')
      ) {
        const token = window.localStorage.getItem('accessToken')
        if (token) {
          ;({ customerId } = decodeToken(token))
        }
      }
      if (ga && ga.trackingId) {
        safeGtag('config', ga.trackingId, {
          page_location: window.location.href,
          ...(customerId
            ? {
                user_id: customerId,
              }
            : {}),
        })
      }
      if (GA_TRACKING_ID) {
        GA_TRACKING_ID.split(';').map((tag) => {
          safeGtag('config', tag, {
            page_location: window.location.href,
            ...(customerId
              ? {
                  user_id: customerId,
                }
              : {}),
          })
        })
      }
      safeFbq('track', 'PageView')
      // trigger page viewed for segment
      if (meta?.customerSegmentEnabled) {
        let item: { name: string } | undefined
        // check this condition to detect customer has accessed to production details route
        if (NextRouter.router?.query.productId) {
          const id = NextRouter.router.query.productId
          const menu = menuData || []
          for (const cat of menu) {
            item = cat?.items?.find((catItem) => catItem.id === id)
            if (item) break
          }
        }
        safeSegment((analytics) =>
          analytics.page(
            undefined,
            undefined,
            {
              url: window.location.href,
              // sometime document.title don't have the name of production
              title: item && item.name ? item.name : document.title,
              path: window.location.pathname,
              referrer: document.referrer,
              country: meta.menuCountry,
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name,
              },
              accountId: meta?.account?.id,
              accountName: meta?.account?.name,
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name,
            },
            {
              context: { groupId: meta?.brand?.id },
            }
          )
        )
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyViewContent = (productId?: string) => {
    try {
      // Only run on client side to prevent hydration errors
      if (typeof window === 'undefined') return

      const id = productId || (NextRouter.router?.query.productId as string)
      const menu = menuData || []
      const { currencyCode } = meta || {}
      let category: { name: string } | undefined
      let item: Oms.MenuItem | undefined

      for (const cat of menu) {
        item = cat?.items?.find?.((catItem) => catItem.id === id)
        if (item) {
          category = cat
          break
        }
      }

      if (item && category && meta) {
        safeFbq('track', 'ViewContent', {
          value: item.variants?.[0]?.price,
          currency: meta.currencyCode,
          content_name: item.name,
          content_type: 'product',
          content_ids: [item.id],
        })

        const viewContentItemPayload = {
          item_id: item.id,
          item_name: item.name,
          item_brand: meta.menu.name,
          item_category: category.name,
          quantity: 1,
          price: item.variants?.[0]?.price,
        }
        safeGtag('event', 'view_item', {
          items: [viewContentItemPayload],
          currency: currencyCode,
          value: item.variants?.[0]?.price,
        })
        if (meta.customerSegmentEnabled) {
          safeSegment((analytics) =>
            analytics.track(
              'Product Viewed',
              {
                app: {
                  id: ODDLE_PASS_CLIENT_ID,
                  name,
                },
                productId: viewContentItemPayload.item_id,
                category: viewContentItemPayload.item_category,
                name: viewContentItemPayload.item_name,
                brand: viewContentItemPayload.item_brand,
                price: viewContentItemPayload.price,
                quantity: viewContentItemPayload.quantity,
                value:
                  (viewContentItemPayload.price || 0) *
                  (viewContentItemPayload.quantity || 0),
                currency: meta.currencyCode,
                menuId: meta.menu.id,
                accountId: meta.menu?.account?.id,
                url: window.location.href,
                ...(item?.imageFullUrls?.[0]
                  ? {
                      imageUrl: `${meta.imagePath}product/${item.imageFullUrls[0]}.jpg`,
                    }
                  : item?.imageUrls?.[0]
                  ? {
                      imageUrl: `${meta.imagePath}product/${item.imageUrls[0]}.jpg`,
                    }
                  : {}),
                country: meta.menuCountry,
                accountName: meta?.account?.name,
                brandId: meta?.brand?.id,
                brandName: meta?.brand?.name,
              },
              {
                context: { groupId: meta?.brand?.id },
              }
            )
          )
        }
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  // const notifyInitiateCheckout = () => {
  //   try {
  //     if (isBot) return
  //
  //     const menu = menuData
  //     const storeOrderData =
  //       checkoutMetaData?.storeOrder || ({} as Oms.StoreOrder)
  //     const { currencyCode } = meta || {}
  //
  //     const orderItems =
  //       checkoutMetaData?.storeOrder?.orderItems || ([] as Oms.OrderItem[])
  //     const fullDetailedOrderItems = orderItems.map((order) => ({
  //       order,
  //       product: getFullProductDetails(
  //         menu as Oms.MenuCategory[],
  //         { itemReference: order.itemId } as Oms.OrderItem
  //       ),
  //     }))
  //     safeFbq('track', 'InitiateCheckout', {
  //       value: storeOrderData.total,
  //       currency: storeOrderData.currencyCode,
  //       num_items: getOrderItemQuantity(
  //         orderItems.map((item) => ({
  //           ...item,
  //           status: 1,
  //           isValid: true,
  //           isPromotionalItem: false,
  //           itemReference: item.itemId,
  //           details: {
  //             imageUrls: [],
  //             imageFullUrls: [],
  //           },
  //           subitemChoices: item.subitemChoices?.map((choice) => ({
  //             ...choice,
  //             status: 1,
  //             name: choice.name,
  //             price: choice.price,
  //             quantity: choice.quantity,
  //           })),
  //         }))
  //       ),
  //       contents: fullDetailedOrderItems.map(({ order, product }) => ({
  //         id: product.itemId,
  //         quantity: order.quantity,
  //         item_price: order.price,
  //       })),
  //     })
  //     const checkoutPayload = fullDetailedOrderItems.map(
  //       ({ order, product }) => ({
  //         item_id: product.itemId,
  //         item_name: order.name,
  //         item_brand: meta?.menu?.name,
  //         item_category: (
  //           menu.find((category) =>
  //             category.items.find((item) => item.id === product.itemId)
  //           ) || {}
  //         ).name,
  //         quantity: order.quantity,
  //         price: order.price,
  //       })
  //     )
  //     safeGtag('event', 'begin_checkout', {
  //       items: checkoutPayload,
  //       currency: currencyCode,
  //       value: storeOrderData.total,
  //     })
  //   } catch (ex) {
  //     console.error(ex)
  //   }
  // }

  const notifyPurchase = (storeOrderData?: Oms.StoreOrder | null) => {
    try {
      if (isObjectEmpty(storeOrderData) || !storeOrderData) {
        return
      }
      if (isBot) return

      const taxAndCharges = storeOrderData.taxAndCharges || []
      const orderItems = storeOrderData.orderItems || []
      safeFbq('track', 'Purchase', {
        value: storeOrderData.total,
        currency: storeOrderData.currencyCode,
        num_items: getOrderItemQuantity(orderItems),
        contents: orderItems.map((order) => {
          const productDetails = getFullProductDetails(menuData, order)
          return {
            id: productDetails.itemId,
            quantity: order.quantity,
            item_price: order.price,
          }
        }),
      })
      const purchasePayload = {
        transaction_id: storeOrderData.id,
        affiliation: storeOrderData.menuName,
        value: storeOrderData.total,
        shipping: storeOrderData.deliveryCost,
        tax: taxAndCharges.reduce((sum, t) => sum + t.amount, 0),
        currency: storeOrderData.currencyCode,
        items: orderItems.map((order) => ({
          item_id: order.itemId,
          item_name: order.name,
          quantity: order.quantity,
          price: order.price,
          item_brand: menuDetailsInfo?.menu?.name,
          item_category: (
            menuData?.find((category) =>
              category.items.find((item) => item.id === order.itemId)
            ) || {}
          ).name,
        })),
      }
      safeGtag('event', 'purchase', purchasePayload)
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerCheckoutStarted = (segment: Record<string, unknown>) => {
    try {
      if (isBot) return
      if (!isObjectEmpty(segment || {})) {
        safeSegment((analytics) =>
          analytics.track(
            'Checkout Started',
            {
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name,
              },
              country: meta?.menuCountry,
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name,
              ...segment,
              oddlePassFirstOrder: undefined,
              oddlePassFirstMenuOrder: undefined,
              submittedDate: undefined,
              orderNumber: undefined,
              oddleEatsReferrer: undefined,
              value: segment.total,
              total: undefined,
            },
            {
              context: { groupId: meta?.brand?.id },
            }
          )
        )
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerSignedOut = ({ country }: { country: string }) => {
    if (meta?.customerSegmentEnabled) {
      safeSegment((analytics) => {
        analytics.track(
          'Signed Out',
          {
            app: {
              id: ODDLE_PASS_CLIENT_ID,
              name,
            },
            country,
            accountId: meta?.account?.id,
            accountName: meta?.account?.name,
            brandId: meta?.brand?.id,
            brandName: meta?.brand?.name,
          },
          {
            context: { groupId: meta?.brand?.id },
          }
        )
        analytics.reset()
      })
    }
  }

  const segmentTrackerSignedIn = (payload: {
    id: string
    email: string
    firstName: string
    lastName: string
    phone: string
    continueWithOddlePass: boolean
    country: string
    phoneIsVerified: boolean
    authMethod: string
    action?: string
  }) => {
    try {
      if (meta?.customerSegmentEnabled) {
        const {
          id,
          email,
          firstName,
          lastName,
          phone,
          continueWithOddlePass,
          country,
          phoneIsVerified,
          authMethod,
        } = payload
        if (payload.action !== 'verification') {
          safeSegment((analytics) => {
            analytics.identify(id, {
              email,
              firstName,
              lastName,
              phone,
              country,
              phoneIsVerified,
            })
            analytics.track(
              'Signed In',
              {
                app: {
                  id: ODDLE_PASS_CLIENT_ID,
                  name,
                },
                email,
                firstName,
                lastName,
                phone,
                continueWithOddlePass,
                country,
                accountId: meta?.account?.id,
                accountName: meta?.account?.name,
                brandId: meta?.brand?.id,
                brandName: meta?.brand?.name,
                phoneIsVerified,
                authMethod,
              },
              {
                context: { groupId: meta?.brand?.id },
              }
            )
          })
        }
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerMenuViewed = () => {
    try {
      // Only run on client side to prevent hydration errors
      if (typeof window === 'undefined') return

      if (meta?.customerSegmentEnabled) {
        safeSegment((analytics) =>
          analytics.track(
            'Menu Viewed',
            {
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name,
              },
              id: meta.menu.id,
              name: meta.menu.name,
              url: window.location.href,
              country: meta.menuCountry,
              accountId: meta?.account?.id,
              accountName: meta?.account?.name,
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name,
            },
            {
              context: { groupId: meta?.brand?.id },
            }
          )
        )
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyAddToCart = (payload: {
    menuItem: {
      id: string
      name: string
      imageFullUrls?: string[]
      imageUrls?: string[]
    }
    orderItem: {
      quantity: number
    }
    finalPrice: number
  }) => {
    try {
      // Only run on client side to prevent hydration errors
      if (typeof window === 'undefined') return

      const category = menuData?.find((category) =>
        category.items.find((item) => item.id === payload.menuItem.id)
      )
      safeFbq('track', 'AddToCart', {
        value: payload.orderItem.quantity * payload.finalPrice,
        currency: meta?.currencyCode,
        content_name: payload.menuItem.name,
        content_type: 'product',
        contents: [
          {
            id: payload.menuItem.id,
            quantity: payload.orderItem.quantity,
            item_price: payload.finalPrice,
          },
        ],
      })
      const addToCartItemPayload = {
        item_id: payload.menuItem.id,
        item_name: payload.menuItem.name,
        item_brand: meta?.menu?.name,
        item_category: category?.name,
        quantity: payload.orderItem.quantity,
        price: payload.finalPrice,
      }
      safeGtag('event', 'add_to_cart', {
        items: [addToCartItemPayload],
        currency: meta?.currencyCode,
        value: payload.finalPrice,
      })
      if (meta?.customerSegmentEnabled) {
        safeSegment((analytics) =>
          analytics.track(
            'Product Added',
            {
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name,
              },
              productId: addToCartItemPayload.item_id,
              category: addToCartItemPayload.item_category,
              name: addToCartItemPayload.item_name,
              brand: addToCartItemPayload.item_brand,
              price: addToCartItemPayload.price,
              quantity: addToCartItemPayload.quantity,
              value: addToCartItemPayload.price * addToCartItemPayload.quantity,
              currency: meta?.currencyCode,
              menuId: meta?.menu?.id,
              accountId: meta?.menu?.account?.id,
              url: window.location.href,
              ...(payload.menuItem.imageFullUrls?.[0]
                ? {
                    imageUrl: `${meta.imagePath}product/${payload.menuItem.imageFullUrls[0]}.jpg`,
                  }
                : payload.menuItem.imageUrls?.[0]
                ? {
                    imageUrl: `${meta.imagePath}product/${payload.menuItem.imageUrls[0]}.jpg`,
                  }
                : {}),
              country: meta.menuCountry,
              accountName: meta?.account?.name,
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name,
            },
            {
              context: { groupId: meta?.brand?.id },
            }
          )
        )
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyNewsletterCompleteRegistration = () => {
    safeFbq('track', 'CompleteRegistration', { content_name: 'newsletter' })
    safeGtag('event', 'complete_registration', {
      event_category: 'engagement',
      event_label: 'newsletter',
    })
  }

  const notifyNewsletterLead = () => {
    safeFbq('track', 'Lead', { content_name: 'newsletter' })
    safeGtag('event', 'lead', {
      event_category: 'engagement',
      event_label: 'newsletter',
    })
  }

  // For non Oddle Pass shop, it is impossible to differentiate between Facebook login or registration since we use the same API and the whole process is invisible to Frontend
  const notifyAccountCompleteRegistration = (payload: {
    facebookId?: string
  }) => {
    safeFbq('track', 'CompleteRegistration', { content_name: 'account' })
    safeGtag('event', 'sign_up', {
      method: payload.facebookId ? 'facebook' : 'email',
    })
  }

  const notifyAccountLead = () => {
    safeFbq('track', 'Lead', { content_name: 'account' })
    safeGtag('event', 'lead', {
      event_category: 'engagement',
      event_label: 'account',
    })
  }

  const segmentTrackerOrderReminderSaved = (payload: Oms.StoreOrder) => {
    try {
      const products = (payload.orderItems || []).map((item) => {
        return {
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          id: item.id,
          orderTags: item.subitemChoices
            ? item.subitemChoices.map((x) => ({
                id: x.name,
                name: x.name,
                price: x.price,
                amount: Number((x.price * x.quantity).toFixed(2)),
                quantity: x.quantity,
              }))
            : null,
        }
      })
      const amount = {
        charges: (payload.taxAndCharges || [])
          .filter((item) => !item.description.includes('GST'))
          .reduce((sum, t) => sum + t.amount, 0),
        discount: (payload.orderPromotions || []).reduce(
          (sum, t) => sum + t.amount,
          0
        ),
        tax:
          (payload.taxAndCharges || []).filter((item) =>
            item.description.includes('GST')
          )?.[0]?.amount || 0,
        taxAndCharges: (payload.taxAndCharges || []).reduce(
          (sum, t) => sum + t.amount,
          0
        ),
        amountProducts: (products || []).reduce((sum, t) => sum + t.price, 0),
      }
      if (meta?.customerSegmentEnabled) {
        safeSegment((analytics) =>
          analytics.track('Order Reminder Saved', {
            app: {
              id: ODDLE_PASS_CLIENT_ID,
              name,
            },
            orderNumber: payload.orderNumber,
            isPickup: payload.isPickup,
            submittedDate: payload.orderDate,
            accountId: meta.menu?.account?.id,
            accountName: meta.menu.email,
            menuId: meta.menu.id,
            menuName: meta.menu.name,
            storeId: payload.storeId,
            storeName: payload.storeName,
            promoCode: payload?.orderPromotions
              ? payload?.orderPromotions?.[0]?.promoCode
              : '',
            orderId: payload.id,
            charge: amount.charges,
            delivery: payload.deliveryCost,
            deliveryWithTaxCharge: amount.taxAndCharges + payload.deliveryCost,
            discount: amount.discount,
            discountWithTaxCharge: amount.discount + amount.tax,
            subTotal: payload.subtotal,
            subTotalWithCharge: payload.subtotal + amount.tax,
            tax: amount.tax,
            total: payload.total,
            currency: payload.currencyCode,
            products: {
              productItems: products,
              amount: amount.amountProducts,
            },
          })
        )
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerOrderedShare = (
    payload: Oms.StoreOrder & { appName: string }
  ) => {
    try {
      if (meta?.customerSegmentEnabled) {
        const products = (payload.orderItems || []).map((item) => {
          return {
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            id: item.id,
            orderTags: item.subitemChoices
              ? item.subitemChoices.map((x) => ({
                  id: x.name,
                  name: x.name,
                  price: x.price,
                  amount: Number((x.price * x.quantity).toFixed(2)),
                  quantity: x.quantity,
                }))
              : null,
          }
        })
        const amount = {
          charges: (payload.taxAndCharges || [])
            .filter((item) => !item.description.includes('GST'))
            .reduce((sum, t) => sum + t.amount, 0),
          discount: (payload.orderPromotions || []).reduce(
            (sum, t) => sum + t.amount,
            0
          ),
          tax:
            (payload.taxAndCharges || []).filter((item) =>
              item.description.includes('GST')
            )?.[0]?.amount || 0,
          taxAndCharges: (payload.taxAndCharges || []).reduce(
            (sum, t) => sum + t.amount,
            0
          ),
          amountProducts: (products || []).reduce((sum, t) => sum + t.price, 0),
        }
        safeSegment((analytics) =>
          analytics.track('Order Shared', {
            app: {
              id: ODDLE_PASS_CLIENT_ID,
              name,
            },
            orderNumber: payload.orderNumber,
            isPickup: payload.isPickup,
            submittedDate: payload.orderDate,
            accountId: meta.menu?.account?.id,
            accountName: meta.menu.email,
            menuId: meta.menu.id,
            menuName: meta.menu.name,
            storeId: payload.storeId,
            storeName: payload.storeName,
            promoCode: payload?.orderPromotions
              ? payload?.orderPromotions[0]?.promoCode
              : '',
            orderId: payload.id,
            charge: amount.charges,
            delivery: payload.deliveryCost,
            deliveryWithTaxCharge: amount.taxAndCharges + payload.deliveryCost,
            discount: amount.discount,
            discountWithTaxCharge: amount.discount + amount.tax,
            subTotal: payload.subtotal,
            subTotalWithCharge: payload.subtotal + amount.tax,
            tax: amount.tax,
            total: payload.total,
            currency: payload.currencyCode,
            products: {
              productItems: products,
              amount: amount.amountProducts,
            },
            orderShareDestination: payload.appName,
          })
        )
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const segmentTrackerOrderViewed = (
    payload: Oms.StoreOrder & { appName?: string; isPurchaser: boolean }
  ) => {
    try {
      if (meta?.customerSegmentEnabled) {
        const products = (payload.orderItems || []).map((item) => {
          return {
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            id: item.id,
          }
        })
        const amount = {
          charges: (payload.taxAndCharges || [])
            .filter((item) => !item.description.includes('GST'))
            .reduce((sum, t) => sum + t.amount, 0),
          discount: (payload.orderPromotions || []).reduce(
            (sum, t) => sum + t.amount,
            0
          ),
          tax:
            (payload.taxAndCharges || []).filter((item) =>
              item.description.includes('GST')
            )?.[0]?.amount || 0,
          taxAndCharges: (payload.taxAndCharges || []).reduce(
            (sum, t) => sum + t.amount,
            0
          ),
          amountProducts: (products || []).reduce((sum, t) => sum + t.price, 0),
        }
        safeSegment((analytics) =>
          analytics.track(
            'Order Viewed',
            {
              isPurchaser: payload.isPurchaser,
              app: {
                id: ODDLE_PASS_CLIENT_ID,
                name,
              },
              orderNumber: payload.orderNumber,
              isPickup: payload.isPickup,
              submittedDate: payload.orderDate,
              accountId: meta.menu?.account?.id,
              accountName: meta.menu.email,
              menuId: meta.menu.id,
              menuName: meta.menu.name,
              storeId: payload.storeId,
              storeName: payload.storeName,
              promoCode: payload?.orderPromotions
                ? payload?.orderPromotions[0]?.promoCode
                : '',
              orderId: payload.id,
              charge: amount.charges,
              delivery: payload.deliveryCost,
              deliveryWithTaxCharge:
                amount.taxAndCharges + payload.deliveryCost,
              discount: amount.discount,
              discountWithTaxCharge: amount.discount + amount.tax,
              subTotal: payload.subtotal,
              subTotalWithCharge: payload.subtotal + amount.tax,
              tax: amount.tax,
              total: payload.total,
              currency: payload.currencyCode,
              products: {
                productItems: products,
                amount: amount.amountProducts,
              },
              brandId: meta?.brand?.id,
              brandName: meta?.brand?.name,
            },
            {
              context: { groupId: meta?.brand?.id },
            }
          )
        )
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyViewCart = (storeOrderData: Oms.StoreOrder) => {
    try {
      const payload = {
        currency: storeOrderData.currencyCode,
        value: storeOrderData.total,
        items: storeOrderData.orderItems?.map((orderItem) => ({
          item_id: orderItem.itemId,
          item_name: orderItem.name,
          quantity: orderItem.quantity,
          price: orderItem.price,
          item_brand: meta?.menu?.name,
          item_category: (
            menuData?.find((category) =>
              category.items.find((item) => item.id === orderItem.itemId)
            ) || {}
          ).name,
        })),
      }
      safeGtag('event', 'view_cart', payload)
    } catch (ex) {
      console.error(ex)
    }
  }

  const notifyLogin = (method: string) => {
    try {
      safeGtag('event', 'login', { method })
    } catch (ex) {
      console.error(ex)
    }
  }

  return {
    notifyPageView,
    notifyViewContent,
    // notifyInitiateCheckout,
    notifyPurchase,
    segmentTrackerCheckoutStarted,
    segmentTrackerSignedOut,
    segmentTrackerSignedIn,
    segmentTrackerMenuViewed,
    segmentTrackerOrderReminderSaved,
    segmentTrackerOrderedShare,
    segmentTrackerOrderViewed,
    notifyAddToCart,
    notifyNewsletterCompleteRegistration,
    notifyNewsletterLead,
    notifyAccountCompleteRegistration,
    notifyAccountLead,
    notifyViewCart,
    notifyLogin,
  }
}

export default useTracker
